import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { retryWithBackoff } from '@/utils/retry';
import { captureEvent, identifyUser, resetIdentity, EVENTS } from '@/lib/posthog';
import {
  clearGleapIdentity,
  identifyGleapAnonymous,
  identifyGleapUser,
} from '@/lib/gleap';
import { logError } from '@/utils/logger';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Identify user in PostHog/Gleap if already authenticated
        if (session?.user) {
          identifyUser(session.user);
          identifyGleapUser(session.user);
        } else {
          identifyGleapAnonymous();
        }
      })
      .catch((error) => {
        logError('Failed to get session:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Identify user in PostHog/Gleap when authenticated
      if (session?.user) {
        identifyUser(session.user);
        identifyGleapUser(session.user);
      } else {
        identifyGleapAnonymous();
      }

      // Handle PASSWORD_RECOVERY event if needed:
      if (event === 'PASSWORD_RECOVERY') {
        // You might store the token in sessionStorage if needed.
        // For example: sessionStorage.setItem('resetToken', token);
        // But note: we're not calling navigate here.
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await retryWithBackoff(() =>
      supabase.auth.signInWithPassword({ email, password })
    );
    if (error) throw error;

    // Identify user and capture sign-in event
    if (data?.user) {
      identifyUser(data.user);
      identifyGleapUser(data.user);
    }
    captureEvent(EVENTS.USER_SIGNED_IN);
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await retryWithBackoff(() =>
      supabase.auth.signUp({ email, password })
    );
    if (error) throw error;

    // Identify user and capture sign-up event
    if (data?.user) {
      identifyUser(data.user);
      identifyGleapUser(data.user);
    }
    captureEvent(EVENTS.USER_SIGNED_UP);
  };

  const signOut = async () => {
    // Capture sign-out event before resetting identity
    captureEvent(EVENTS.USER_SIGNED_OUT);

    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    setSession(null);
    setUser(null);

    // Reset PostHog identity after sign-out
    resetIdentity();
    clearGleapIdentity();
    identifyGleapAnonymous();
  };

  const resetPassword = async (email: string) => {
    const redirectURL = `${import.meta.env.VITE_APP_URL || window.location.origin}/auth/reset-password`;
    const { error } = await retryWithBackoff(() =>
      supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectURL })
    );
    if (error) throw error;

    captureEvent(EVENTS.PASSWORD_RESET_REQUESTED);
  };

  const refreshUser = async () => {
    const { data: { user: refreshedUser }, error } = await supabase.auth.getUser();
    if (error) {
      logError('Failed to refresh user:', error);
      throw error;
    }
    setUser(refreshedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isLoading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
